import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { SlideProps } from "@/engine/app/offsite-config";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { teamLabel } from "@/engine/app/team-label";
import { useStore } from "@/state/store";
import { api } from "@/api/client";
import { TEAM_FARBEN } from "./Slide13Teams";

interface TeamThema {
  name: string | null;
  thema: string;
  problem: string;
}

/**
 * Abschluss der Themenfindung (seit Umplanung 24.09. am Morgen von Tag 2):
 * Die Teams haben am Handy „Euer Anwendungsfall" (Feld thema) eingetragen —
 * die Folie zeigt alle sechs live, damit der Raum sie gemeinsam anschaut,
 * bevor das Prompten beginnt. Danach geht es am selben Steckbrief weiter.
 *
 * Solange die Arbeitsphase läuft, bietet die Folie dem Moderator einen
 * dezenten Knopf „Themenfindung beenden": Phase → waiting, die Handys
 * pausieren, der Timer wird abgeräumt — das Prompten startet mit frischer Uhr.
 */
export function Slide13bThemen(_: SlideProps) {
  const { festeTeams } = useOffsiteConfig();
  const anzahl = festeTeams ?? 6;
  const { state, dispatch } = useStore();
  const token = state.auth.token;
  const istPresenter = state.auth.role === "presenter";
  const phase = state.workshop.phase.phase;
  const themen = useThemen(istPresenter);
  const [beende, setBeende] = useState(false);

  const beenden = async () => {
    setBeende(true);
    try {
      // Die Uhr gehört dem Moderator und überlebt Phasenwechsel — erst
      // ausdrücklich abräumen, sonst läuft die Themenfindungs-Uhr in Tag 2
      // hinein und „Timer starten" fehlt (E2E-Lauf 8, 23.09.).
      await api.admin.resetTimer(token);
      const p = await api.phase.set(token, "waiting");
      dispatch({ type: "phase/set", phase: p });
    } finally {
      setBeende(false);
    }
  };

  const mitThema = [...themen.values()].filter((t) => t.thema).length;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "clamp(12px, 2.2vh, 24px)",
        background: "var(--ink-900, #00103a)",
        color: "var(--ink-100, #e8edf7)",
        padding: "clamp(20px, 3.7vh, 40px) 72px 112px",
      }}
    >
      {istPresenter && phase === "working" && (
        <button
          type="button"
          onClick={() => void beenden()}
          disabled={beende}
          title="Handys pausieren, Timer abräumen — danach Folie 20 und Prompten am selben Steckbrief"
          style={{
            position: "absolute",
            top: 20,
            right: 24,
            background: "transparent",
            color: "rgba(255,255,255,0.45)",
            border: "1px solid rgba(255,255,255,0.18)",
            borderRadius: 6,
            padding: "6px 12px",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          {beende ? "…" : "Themenfindung beenden"}
        </button>
      )}

      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: "clamp(14px, 1.85vh, 20px)", letterSpacing: "0.2em", textTransform: "uppercase", opacity: 0.7 }}>
          Eure Anwendungsfälle
        </div>
        <div style={{ fontSize: "clamp(28px, 4.4vh, 48px)", fontWeight: 700, marginTop: 4 }}>
          Daran arbeitet ihr jetzt weiter
        </div>
        <div style={{ fontSize: "clamp(14px, 1.7vh, 18px)", marginTop: 4, opacity: 0.6 }}>
          {mitThema} von {anzahl} Teams haben ihr Thema — alles ist gespeichert.
        </div>
      </div>

      <ThemenKacheln themen={themen} anzahl={anzahl} />
    </div>
  );
}

/**
 * Thema in voller Länge: startet bei der Wunschgröße und verkleinert die
 * Schrift, bis der Text in die Kachel passt — auch am kleinen Beamer.
 */
function PasstText({ text, leer }: { text: string; leer: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [hoehe, setHoehe] = useState(0);

  const [takt, setTakt] = useState(0);

  // Neu messen bei Größenänderung, nach dem Laden der Webfont (breiter als
  // der Fallback — sonst passt es beim ersten Messen und später nicht mehr)
  // und sicherheitshalber alle paar Sekunden mit dem Daten-Takt.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setHoehe(el.clientWidth * 10000 + el.clientHeight));
    ro.observe(el);
    let aktiv = true;
    void document.fonts?.ready.then(() => aktiv && setTakt((t) => t + 1));
    const id = setInterval(() => setTakt((t) => t + 1), 3000);
    return () => {
      aktiv = false;
      ro.disconnect();
      clearInterval(id);
    };
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let px = Math.min(28, Math.max(13, window.innerHeight * 0.026));
    el.style.fontSize = `${px}px`;
    while ((el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1) && px > 10) {
      px -= 0.5;
      el.style.fontSize = `${px}px`;
    }
  }, [text, hoehe, takt]);

  return (
    <div
      ref={ref}
      style={{
        flex: "1 1 0",
        minHeight: 0,
        overflow: "hidden",
        lineHeight: 1.3,
        fontWeight: 600,
        marginTop: "clamp(4px, 0.9vh, 10px)",
        opacity: leer ? 0.4 : 1,
        overflowWrap: "anywhere",
        hyphens: "auto",
      }}
    >
      {text}
    </div>
  );
}

/** Teamname + Thema je Team, alle 5 s frisch (nur mit Presenter-Token). */
function useThemen(aktivWenn: boolean) {
  const { state } = useStore();
  const token = state.auth.token;
  const istPresenter = aktivWenn;
  const [themen, setThemen] = useState<Map<number, TeamThema>>(new Map());

  useEffect(() => {
    if (!istPresenter) return;
    let aktiv = true;
    const lade = async () => {
      try {
        const { teams } = await api.teams.list(token);
        const eintraege = await Promise.all(
          teams.map(async (t) => {
            const r = await api.teams.getReq(token, t.number).catch(() => null);
            const f = r?.requirement?.fields;
            return [
              t.number,
              { name: t.name, thema: (f?.thema ?? "").trim(), problem: (f?.problem ?? "").trim() },
            ] as const;
          }),
        );
        if (aktiv) setThemen(new Map(eintraege));
      } catch {
        /* nächster Takt */
      }
    };
    void lade();
    const id = setInterval(lade, 5000);
    return () => {
      aktiv = false;
      clearInterval(id);
    };
  }, [token, istPresenter]);
  return themen;
}

function ThemenKacheln({ themen, anzahl }: { themen: Map<number, TeamThema>; anzahl: number }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        // Kacheln füllen die Resthöhe, damit lange Themen ganz hineinpassen.
        gridAutoRows: "1fr",
        flex: "1 1 0",
        minHeight: 0,
        gap: 18,
        width: "100%",
        maxWidth: 1600,
        margin: "0 auto",
      }}
    >
      {Array.from({ length: anzahl }, (_, i) => i + 1).map((n) => {
        const t = themen.get(n);
        const farbe = TEAM_FARBEN[(n - 1) % TEAM_FARBEN.length];
        return (
          <div
            key={n}
            data-testid={`thema-team-${n}`}
            style={{
              borderTop: `4px solid ${farbe}`,
              background: "rgba(255,255,255,0.05)",
              borderRadius: 10,
              padding: "clamp(10px, 1.5vh, 16px) clamp(14px, 1.3vw, 22px)",
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ fontSize: 13, letterSpacing: "0.12em", textTransform: "uppercase", color: farbe, fontWeight: 700 }}>
              {teamLabel(n)}
            </div>
            <div style={{ fontSize: 16, opacity: 0.7, marginTop: 2 }}>{t?.name ?? "— noch kein Name —"}</div>
            <PasstText text={t?.thema || "Thema folgt …"} leer={!t?.thema} />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Leinwand „Anforderung", solange die Uhr noch nicht läuft: die sechs Themen
 * als Einstieg; darunter bietet die Arbeitsphase den Startknopf.
 */
export function ThemenVorschau() {
  const { festeTeams } = useOffsiteConfig();
  const themen = useThemen(true);
  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%", height: "min(52vh, 560px)" }}>
      <ThemenKacheln themen={themen} anzahl={festeTeams ?? 6} />
    </div>
  );
}
